import { combineReducers, configureStore, isFulfilled, isPending, isRejected, Middleware } from '@reduxjs/toolkit';
import { getOperatingContext } from '../utils/operatingContextLifecycle';
import authReducer from './slices/authSlice';
import userReducer from './slices/userSlice';
import guestReducer from './slices/guestSlice';
import menuReducer from './slices/menuSlice';
import orderReducer from './slices/orderSlice';
import allergenReducer from './slices/allergenSlice';
import productReducer from './slices/productSlice';
import semiFinishedReducer from './slices/semiFinishedSlice';
import dishReducer from './slices/dishSlice';
import inventoryReducer from './slices/inventorySlice';
import eventOrderReducer from './slices/eventOrderSlice';
import purchaseReducer from './slices/purchaseSlice';
import preparationOperationReducer from './slices/preparationOperationSlice';

const reducers = combineReducers({
    auth: authReducer,
    user: userReducer,
    guest: guestReducer,
    menu: menuReducer,
    order: orderReducer,
    allergen: allergenReducer,
    product: productReducer,
    semiFinished: semiFinishedReducer,
    dish: dishReducer,
    inventory: inventoryReducer,
    eventOrder: eventOrderReducer,
    purchase: purchaseReducer,
    preparationOperation: preparationOperationReducer,
});

const contextRequests = new Map<string, { token: string | null; generation: number }>();
const operatingContextMiddleware: Middleware = () => next => action => {
  if (isPending(action) && !action.type.startsWith('auth/')) {
    contextRequests.set(action.meta.requestId, {
      token: localStorage.getItem('token'), generation: getOperatingContext().generation,
    });
  } else if (isFulfilled(action) || isRejected(action)) {
    const captured = contextRequests.get(action.meta.requestId);
    contextRequests.delete(action.meta.requestId);
    if (captured && (captured.token !== localStorage.getItem('token')
      || captured.generation !== getOperatingContext().generation)) return action;
  }
  return next(action);
};

export const store = configureStore({
  reducer: (state: ReturnType<typeof reducers> | undefined, action) => {
    if (state && (action.type === 'operatingContext/reset' || action.type === 'auth/logout'
      || action.type === 'auth/login/fulfilled' || action.type === 'auth/register/fulfilled')) {
      return reducers({ ...reducers(undefined, { type: '@@INIT' }), auth: state.auth }, action);
    }
    return reducers(state, action);
  },
  middleware: getDefaultMiddleware => getDefaultMiddleware().concat(operatingContextMiddleware),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
